import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, MapPin, Phone, Mail } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../Config/Config';
import logo from '../assets/westberglogo2png.png';
const FacebookIcon = ({ size = 18, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
);

const TwitterIcon = ({ size = 18, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path></svg>
);

const InstagramIcon = ({ size = 18, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
);

const LinkedinIcon = ({ size = 18, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect x="2" y="9" width="4" height="12"></rect><circle cx="4" cy="4" r="2"></circle></svg>
);

const YoutubeIcon = ({ size = 18, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon></svg>
);

const Footer = () => {
  const [footerSettings, setFooterSettings] = useState({
    aboutText: 'Empowering students with premium educational resources, expert guidance, and state-of-the-art facilities to achieve their career goals.',
    facebookUrl: '',
    twitterUrl: '',
    instagramUrl: '',
    linkedinUrl: '',
    youtubeUrl: ''
  });

  const [contactSettings, setContactSettings] = useState({
    addressLine1: 'Office #1515, (Door No. 2/1149/A89)',
    addressLine2: 'Hilite Business Park, Thondayad Bypass Road, Kozhikode - 673014',
    phone1: '+1 (800) 123-4567',
    email1: 'info@westberg.edu'
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [footerSnap, contactSnap] = await Promise.all([
          getDoc(doc(db, 'settings', 'footer')),
          getDoc(doc(db, 'settings', 'contact'))
        ]);

        if (footerSnap.exists()) {
          setFooterSettings(footerSnap.data());
        }
        if (contactSnap.exists()) {
          setContactSettings(contactSnap.data());
        }
      } catch (err) {
        console.error('Error fetching footer/contact settings:', err);
      }
    };
    fetchSettings();
  }, []);

  return (
    <footer className="bg-rich-black text-white/60 py-24 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">

        {/* Brand & About */}
        <div>
          <div className="flex items-center gap-4 mb-8">
            {/* <div className="bg-[#4A0F16] text-cream p-2 flex items-center justify-center border border-white/10 shadow-sm">
              <GraduationCap size={24} />
            </div> */}
            <img src={logo} alt="Westberg Logo" className="h-14 w-auto" />
            {/* <span className="text-2xl font-serif font-bold text-white tracking-tight">WESTBERG</span> */}
          </div>
          <p className="text-white/50 text-sm font-light leading-relaxed mb-8">
            {footerSettings.aboutText}
          </p>
          <div className="flex items-center gap-4 text-white">
            {footerSettings.facebookUrl && <a href={footerSettings.facebookUrl} target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white/10 flex items-center justify-center hover:bg-[#4A0F16] hover:border-[#4A0F16] transition-all rounded-full"><FacebookIcon size={18} /></a>}
            {footerSettings.twitterUrl && <a href={footerSettings.twitterUrl} target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white/10 flex items-center justify-center hover:bg-[#4A0F16] hover:border-[#4A0F16] transition-all rounded-full"><TwitterIcon size={18} /></a>}
            {footerSettings.instagramUrl && <a href={footerSettings.instagramUrl} target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white/10 flex items-center justify-center hover:bg-[#4A0F16] hover:border-[#4A0F16] transition-all rounded-full"><InstagramIcon size={18} /></a>}
            {footerSettings.linkedinUrl && <a href={footerSettings.linkedinUrl} target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white/10 flex items-center justify-center hover:bg-[#4A0F16] hover:border-[#4A0F16] transition-all rounded-full"><LinkedinIcon size={18} /></a>}
            {footerSettings.youtubeUrl && <a href={footerSettings.youtubeUrl} target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white/10 flex items-center justify-center hover:bg-[#4A0F16] hover:border-[#4A0F16] transition-all rounded-full"><YoutubeIcon size={18} /></a>}
          </div>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="text-white font-bold mb-8 uppercase tracking-widest text-[10px]">Quick Links</h4>
          <ul className="space-y-4 text-sm font-light">
            <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
            <li><Link to="/courses" className="hover:text-white transition-colors">Our Courses</Link></li>
            <li><Link to="/admissions" className="hover:text-white transition-colors">Admissions</Link></li>
            <li><Link to="/placements" className="hover:text-white transition-colors">Placements</Link></li>
            <li><Link to="/gallery" className="hover:text-white transition-colors">Gallery</Link></li>
            <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
            <li><Link to="/careers" className="hover:text-white transition-colors">Careers</Link></li>
          </ul>
        </div>

        {/* Contact Info */}
        <div>
          <h4 className="text-white font-bold mb-8 uppercase tracking-widest text-[10px]">Contact Us</h4>
          <ul className="space-y-6 text-sm font-light">
            <li className="flex items-start gap-4">
              <MapPin size={18} className="text-[#8C363D] shrink-0 mt-0.5" />
              <span className="leading-relaxed">
                {contactSettings.addressLine1}
                {contactSettings.addressLine2 && <><br />{contactSettings.addressLine2}</>}
              </span>
            </li>
            <li className="flex items-center gap-4">
              <Phone size={18} className="text-[#8C363D] shrink-0" />
              <span>{contactSettings.phone1}</span>
            </li>
            <li className="flex items-center gap-4">
              <Mail size={18} className="text-[#8C363D] shrink-0" />
              <span>{contactSettings.email1}</span>
            </li>
          </ul>
        </div>

        {/* Newsletter */}
        {/* <div>
          <h4 className="text-white font-bold mb-8 uppercase tracking-widest text-[10px]">Newsletter</h4>
          <p className="text-white/50 text-sm font-light leading-relaxed mb-6">Subscribe to get the latest news, updates, and educational insights directly to your inbox.</p>
          <form className="flex flex-col gap-4">
            <input
              type="email"
              placeholder="Your email address"
              className="px-6 py-4 bg-black/40 border border-white/10 text-sm text-white focus:outline-none focus:border-white/30 transition-colors"
            />
            <button className="px-6 py-4 bg-[#4A0F16] text-white text-[10px] font-bold uppercase tracking-widest hover:bg-[#8C363D] transition-colors border border-white/10 shadow-sm">
              Subscribe Now
            </button>
          </form>
        </div> */}

      </div>

      {/* Bottom Bar */}
      <div className="max-w-7xl mx-auto px-6 mt-24 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between text-[10px] uppercase tracking-widest font-bold text-white/40">
        <p>&copy; {new Date().getFullYear()} WESTBERG Institution. All rights reserved.</p>
        <div className="flex items-center gap-8 mt-6 md:mt-0">
          <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-white transition-colors">Terms & Conditions</Link>
          {/* <Link to="/refund-policy" className="hover:text-white transition-colors">Refund Policy</Link> */}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
